import { useCallback, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { PdfFieldRect, PdfNormalizedRect, PdfDocumentMetrics } from "./types";

interface PdfFieldCanvasProps {
  metrics: PdfDocumentMetrics;
  fields?: PdfFieldRect[];
  drawingEnabled?: boolean;
  selectionEnabled?: boolean;
  onCreateRect?: (rect: PdfNormalizedRect) => void;
  onSelectField?: (field: PdfFieldRect | null) => void;
  className?: string;
  minWidthPx?: number;
  minHeightPx?: number;
}

interface DraftRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function PdfFieldCanvas({
  metrics,
  fields = [],
  drawingEnabled = true,
  selectionEnabled = true,
  onCreateRect,
  onSelectField,
  className,
  minWidthPx = 12,
  minHeightPx = 6,
}: PdfFieldCanvasProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);
  const [draftRect, setDraftRect] = useState<DraftRect | null>(null);

  const displayWidth = metrics.viewport.width * metrics.viewport.scale;
  const displayHeight = metrics.viewport.height * metrics.viewport.scale;

  const normalizeRect = useCallback(
    (rect: DraftRect): PdfNormalizedRect => {
      const x = rect.left / displayWidth;
      const width = rect.width / displayWidth;
      const normalizedTop = rect.top / displayHeight;
      const height = rect.height / displayHeight;

      return {
        x,
        y: 1 - normalizedTop - height,
        width,
        height,
      };
    },
    [displayWidth, displayHeight],
  );

  const normalizedToDisplay = useCallback(
    (rect: PdfNormalizedRect) => {
      const width = rect.width * displayWidth;
      const height = rect.height * displayHeight;
      const left = rect.x * displayWidth;
      const top = displayHeight - (rect.y * displayHeight) - height;

      return { left, top, width, height };
    },
    [displayWidth, displayHeight],
  );

  const fieldHitTest = useCallback(
    (point: { x: number; y: number }) => {
      for (const field of fields) {
        const { left, top, width, height } = normalizedToDisplay(field.rect);
        if (
          point.x >= left &&
          point.x <= left + width &&
          point.y >= top &&
          point.y <= top + height
        ) {
          return field;
        }
      }
      return null;
    },
    [fields, normalizedToDisplay],
  );

  const getRelativePoint = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const surface = surfaceRef.current;
    if (!surface) {
      return { x: 0, y: 0 };
    }

    const bounds = surface.getBoundingClientRect();
    return {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    };
  }, []);

  const resetDraft = useCallback(() => {
    startPointRef.current = null;
    setDraftRect(null);
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!drawingEnabled && !selectionEnabled) {
        return;
      }

      // Check if the event target is a field element (from PdfFieldOverlay)
      // If so, don't handle it - let the overlay handle dragging
      const target = event.target as HTMLElement;
      if (target.closest('[data-field-overlay]')) {
        return;
      }

      const point = getRelativePoint(event);

      if (selectionEnabled && fields.length > 0) {
        const hitField = fieldHitTest(point);
        if (hitField) {
          onSelectField?.(hitField);
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      if (!drawingEnabled) {
        onSelectField?.(null);
        return;
      }

      onSelectField?.(null);
      startPointRef.current = point;
      setDraftRect({ left: point.x, top: point.y, width: 0, height: 0 });
      event.preventDefault();
      event.stopPropagation();
    },
    [drawingEnabled, selectionEnabled, fields.length, getRelativePoint, fieldHitTest, onSelectField],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!drawingEnabled || !startPointRef.current) {
        return;
      }

      const currentPoint = getRelativePoint(event);
      const origin = startPointRef.current;
      const left = Math.min(origin.x, currentPoint.x);
      const top = Math.min(origin.y, currentPoint.y);
      const width = Math.abs(currentPoint.x - origin.x);
      const height = Math.abs(currentPoint.y - origin.y);

      setDraftRect({ left, top, width, height });
      event.preventDefault();
      event.stopPropagation();
    },
    [drawingEnabled, getRelativePoint],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!drawingEnabled || !startPointRef.current || !draftRect) {
        resetDraft();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (draftRect.width >= minWidthPx && draftRect.height >= minHeightPx) {
        onCreateRect?.(normalizeRect(draftRect));
      }

      resetDraft();
    },
    [drawingEnabled, draftRect, minHeightPx, minWidthPx, normalizeRect, onCreateRect, resetDraft],
  );

  const handlePointerLeave = useCallback(() => {
    resetDraft();
  }, [resetDraft]);

  const cursor = useMemo(() => {
    if (drawingEnabled) {
      return "crosshair";
    }
    if (selectionEnabled) {
      return "pointer";
    }
    return "default";
  }, [drawingEnabled, selectionEnabled]);

  return (
    <div
      ref={surfaceRef}
      className={cn("absolute inset-0", className)}
      style={{
        width: displayWidth,
        height: displayHeight,
        cursor,
        zIndex: 5,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
    >
      {draftRect ? (
        <div
          className="absolute border border-emerald-400 bg-emerald-500/10"
          style={{
            left: draftRect.left,
            top: draftRect.top,
            width: draftRect.width,
            height: draftRect.height,
          }}
        />
      ) : null}
    </div>
  );
}

